import {
  collection,
  doc,
  setDoc,
  updateDoc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  ChildSchema,
  ReportSchema,
  emptyContent,
  followUpDate,
  type Child,
  type Report,
  type ReportPhoto,
} from "../types";

function userDoc(uid: string) {
  return doc(db, "users", uid);
}
function childrenCol(uid: string) {
  return collection(db, "users", uid, "children");
}
function reportsCol(uid: string) {
  return collection(db, "users", uid, "reports");
}

// Server-set timestamps come back from Firestore as Timestamp objects;
// our zod schemas use `z.number()` (millis since epoch). Convert at the
// read boundary so the rest of the app works with plain numbers.
// Duck-type check (don't use `instanceof Timestamp` — fails across SDK
// module-instance boundaries and is awkward to mock in tests).
function isTimestamp(v: unknown): v is { toMillis: () => number } {
  return (
    typeof v === "object" &&
    v !== null &&
    "toMillis" in v &&
    typeof (v as { toMillis: unknown }).toMillis === "function"
  );
}

function normaliseTimestamps(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    out[k] = isTimestamp(v) ? v.toMillis() : v;
  }
  return out;
}

export async function ensureUserProfile(uid: string, displayName: string, email: string): Promise<void> {
  const snap = await getDoc(userDoc(uid));
  if (!snap.exists()) {
    await setDoc(userDoc(uid), {
      display_name: displayName,
      email,
      created_at: serverTimestamp(),
    });
  }
}

export async function listChildren(uid: string): Promise<Child[]> {
  const snap = await getDocs(query(childrenCol(uid), orderBy("name_en")));
  return snap.docs.map((d) =>
    ChildSchema.parse({ id: d.id, ...normaliseTimestamps(d.data() as Record<string, unknown>) })
  );
}

export type ChildInput = Omit<Child, "id">;

export async function saveChild(uid: string, childId: string | null, data: ChildInput): Promise<string> {
  if (childId) {
    await updateDoc(doc(db, "users", uid, "children", childId), data);
    return childId;
  }
  const ref = await addDoc(childrenCol(uid), data);
  return ref.id;
}

export async function getReportForMonth(uid: string, childId: string, period: string): Promise<Report | null> {
  const q = query(
    reportsCol(uid),
    where("child_id", "==", childId),
    where("report_period", "==", period),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.docs.length === 0) return null;
  const d = snap.docs[0]!;
  return ReportSchema.parse({ id: d.id, ...normaliseTimestamps(d.data() as Record<string, unknown>) });
}

export async function listReportsForMonth(uid: string, period: string): Promise<Report[]> {
  const q = query(reportsCol(uid), where("report_period", "==", period));
  const snap = await getDocs(q);
  return snap.docs.map((d) =>
    ReportSchema.parse({ id: d.id, ...normaliseTimestamps(d.data() as Record<string, unknown>) })
  );
}

export async function upsertDraftReport(
  uid: string,
  childId: string,
  period: string,
  recordedBy: string,
  photos: ReportPhoto[]
): Promise<string> {
  const existing = await getReportForMonth(uid, childId, period);
  if (existing) {
    // If the user re-picked the SAME photos (same gp_media_ids, e.g. to
    // refresh an expired Picker session), preserve content + status.
    // If different photos, the existing AI draft no longer matches the
    // visual content, so reset to draft and clear content for re-draft.
    // Invalidate cached_main_photo_path either way so /draft re-caches.
    const oldIds = existing.photos.map((p) => p.gp_media_id).sort().join(",");
    const newIds = photos.map((p) => p.gp_media_id).sort().join(",");
    const photosChanged = oldIds !== newIds;
    const updates: Record<string, unknown> = {
      photos,
      cached_main_photo_path: null,
      updated_at: serverTimestamp(),
    };
    if (photosChanged) {
      updates.status = "draft";
      updates.content = emptyContent();
      updates.approved_at = null;
      updates.rendered_at = null;
      updates.pdf_storage_path = null;
      updates.ai_draft_raw = null;
    }
    await updateDoc(doc(db, "users", uid, "reports", existing.id), updates);
    return existing.id;
  }
  const ref = await addDoc(reportsCol(uid), {
    child_id: childId,
    report_period: period,
    recorded_by: recordedBy,
    status: "draft",
    vera_framing_note: null,
    prior_report_id: null,
    photos,
    content: emptyContent(),
    follow_up_date: followUpDate(period),
    ai_draft_raw: null,
    pdf_storage_path: null,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
    approved_at: null,
    rendered_at: null,
  });
  return ref.id;
}

// Reset an approved or rendered report back to draft state, clearing
// content so /draft will accept it and the regenerate flow can run.
// PDF storage path is dropped (the old PDF stays in Storage as an
// orphan — Release 2 has TTL/cleanup as a follow-up).
export async function resetReportToDraft(uid: string, reportId: string): Promise<void> {
  await updateDoc(doc(db, "users", uid, "reports", reportId), {
    status: "draft",
    content: emptyContent(),
    approved_at: null,
    rendered_at: null,
    pdf_storage_path: null,
    ai_draft_raw: null,
    cached_main_photo_path: null,
    updated_at: serverTimestamp(),
  });
}

export async function loadReport(uid: string, reportId: string): Promise<Report | null> {
  const snap = await getDoc(doc(db, "users", uid, "reports", reportId));
  if (!snap.exists()) return null;
  return ReportSchema.parse({ id: snap.id, ...normaliseTimestamps(snap.data() as Record<string, unknown>) });
}

export async function loadChild(uid: string, childId: string): Promise<Child | null> {
  const snap = await getDoc(doc(db, "users", uid, "children", childId));
  if (!snap.exists()) return null;
  return ChildSchema.parse({ id: snap.id, ...normaliseTimestamps(snap.data() as Record<string, unknown>) });
}

export type ReportContentUpdate = Report["content"];

// CONCURRENCY NOTE: last-write-wins. If Vera has the editor open in two
// tabs (phone + laptop), saves race and the second silently overwrites
// the first. For single-user / 16 reports/month this is an accepted
// trade-off. If it bites: add an `updated_at` precondition or a
// `version: number` field with optimistic concurrency.
export async function updateReportContent(
  uid: string,
  reportId: string,
  content: ReportContentUpdate,
  framingNote: string | null
): Promise<void> {
  await updateDoc(doc(db, "users", uid, "reports", reportId), {
    content,
    vera_framing_note: framingNote,
    updated_at: serverTimestamp(),
  });
}

export async function approveReport(uid: string, reportId: string): Promise<void> {
  await updateDoc(doc(db, "users", uid, "reports", reportId), {
    status: "approved",
    approved_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
}

import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";

export async function getRenderedPdfDownloadUrl(storagePath: string): Promise<string> {
  const storage = getStorage();
  const ref = storageRef(storage, storagePath);
  return getDownloadURL(ref);
}
